import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface CuratedPromptCardProps {
  prompt: {
    slug: string;
    title: string;
    prompt: string;
    imageUrl: string;
    category: string | null;
    featured: boolean;
  };
  className?: string;
  index?: number;
}

export function CuratedPromptCard({ prompt, className, index = 999 }: CuratedPromptCardProps) {
  // Enhanced alt text for better image SEO
  const altText = prompt.category
    ? `${prompt.title} - ${prompt.category} AI prompt example`
    : prompt.title;

  // Title attribute with prompt preview
  const titleText = prompt.category
    ? `AI generated ${prompt.category}: ${prompt.prompt.slice(0, 60)}${prompt.prompt.length > 60 ? "..." : ""}`
    : prompt.prompt.slice(0, 80);

  return (
    <Link
      to="/prompts/$slug"
      params={{ slug: prompt.slug }}
      preload="intent"
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border bg-card",
        "transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl cursor-pointer",
        className
      )}
    >
      {prompt.featured && (
        <div className="absolute top-3 right-3 z-10">
          <Badge className="bg-primary text-primary-foreground">Featured</Badge>
        </div>
      )}

      <div className="aspect-square overflow-hidden">
        <img
          src={prompt.imageUrl}
          alt={altText}
          title={titleText}
          loading={index < 4 ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : undefined}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>

      <div className={cn(
        "absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent",
        "opacity-0 transition-opacity duration-300 group-hover:opacity-100",
        "flex flex-col justify-end p-5"
      )}>
        <h3 className="mb-2 text-lg font-semibold line-clamp-1">{prompt.title}</h3>
        <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">{prompt.prompt}</p>
        {prompt.category && (
          <span className="text-xs text-muted-foreground uppercase tracking-wide">
            {prompt.category}
          </span>
        )}
      </div>
    </Link>
  );
}
